import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaSquareFacebook, FaXTwitter, FaInstagram, FaYoutube } from 'react-icons/fa6';

import { SOCIAL_LINKS } from '../../constants';

/**
 * The row of social marks, in the two treatments the design uses:
 *
 *   bare — the footer's, node 16383:21316. Marks alone on the dark ground,
 *          17px apart, each at the size the design gives it.
 *   tile — the contact card's, node 16383:29943. The same marks at 16px,
 *          centred in a 36px circle on the field ground, 12px apart.
 *
 * One component for both so the accounts, their order and their labels cannot
 * drift between the footer and the page it links to. Which accounts appear is
 * `SOCIAL_LINKS`; this only decides how they are drawn.
 *
 * The per-mark sizes are the footer's own — X is drawn smaller than the rest
 * because its glyph fills more of its box — and `tile` overrides them with the
 * single 16px the card uses.
 */

const MARKS = {
    facebook: { Icon: FaSquareFacebook, size: 22 },
    twitter: { Icon: FaXTwitter, size: 18.65 },
    instagram: { Icon: FaInstagram, size: 22 },
    youtube: { Icon: FaYoutube, size: 22 },
};

const VARIANTS = {
    /*
     * The 44px box is the thumb's, not the design's.
     *
     * Drawn as the design has it, a mark is a 22px glyph in a 31x34 box, which
     * is under the 44px a finger needs and sits in the footer, where the next
     * target is 26px away. The box is grown to 44 square and the gap shrunk by
     * exactly what the box gained — 17px between 31px boxes and 3.8px between
     * 44px ones both leave 25.8px between the glyphs themselves — so the row
     * still reads at the spacing the file draws, and only the hit area changes.
     *
     * The pull on the list is the other half of that. A centred glyph now has
     * 11px of box in front of it, which in the footer indented the first mark
     * against the logo and the tagline it lines up under. The negative start
     * margin takes exactly that 11px back, so the row still begins on the
     * column's own edge.
     */
    bare: {
        list: 'tw-gap-[3.8px] tw--ms-[11px]',
        link: 'tw-flex tw-h-[44px] tw-w-[44px] tw-items-center tw-justify-center tw-text-white hover:tw-text-v2-brand-ring',
    },
    /*
     * `h-[36px]`, not `h-9`. The design's circle is 36px, but the app sets a
     * 14px root font, so the named scale lands at 87.5% of its own number and
     * `h-9` painted 31.5 — the note at the foot of `shared/styles/base.css`.
     * An exact figure from the file is written as an exact figure here.
     */
    tile: {
        list: 'tw-gap-3',
        link:
            'tw-flex tw-h-[36px] tw-w-[36px] tw-items-center tw-justify-center tw-rounded-full ' +
            'tw-bg-v2-ground-200 tw-text-v2-text-faint hover:tw-bg-v2-brand-tint hover:tw-text-v2-brand',
    },
};

const SocialLinks = ({ variant = 'bare', className = '' }) => {
    const { t } = useTranslation();
    const treatment = VARIANTS[variant] ?? VARIANTS.bare;

    return (
        <ul className={`tw-flex tw-items-center ${treatment.list} ${className}`}>
            {SOCIAL_LINKS.map(({ key, href }) => {
                const mark = MARKS[key];
                if (!mark) return null;

                return (
                    <li key={key}>
                        <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={t(`v2.social.${key}`)}
                            className={`${treatment.link} tw-transition-colors`}
                        >
                            <mark.Icon size={variant === 'tile' ? 16 : mark.size} aria-hidden />
                        </a>
                    </li>
                );
            })}
        </ul>
    );
};

export default SocialLinks;
